// Client layer (TS-2.5.03) - UC-9..UC-13/UC-24, BRD section 8.5. Composes the generate/publish
// workflow (PM+ only), the version list (which doubles as the archive browser per BRD section
// 8.2), version detail (artifacts/finalize/compare) and the frozen-roadmap snapshot view. Owns
// only navigation-within-page state (which sub-view is showing); every read/write goes through the
// service hooks and publicationApi, never straight to http.
import { useMemo, useState } from "react";
import { PortfolioRoles } from "../../api/generated/contracts";
import type { ListVersionsParams } from "../../api/publication";
import { useCallerPermissions } from "../../hooks/useCallerPermissions";
import { strings } from "../../strings";
import { DeckGeneratePanel } from "./DeckGeneratePanel";
import { DeckVersionDetail } from "./DeckVersionDetail";
import { DeckVersionList } from "./DeckVersionList";
import { FrozenRoadmapPanel } from "./FrozenRoadmapPanel";
import { useDeckVersions } from "./hooks/useDeckVersions";

const PUBLISH_ROLES = [PortfolioRoles.ProjectManager, PortfolioRoles.Admin];

type DecksView = { readonly kind: "list" } | { readonly kind: "detail"; readonly versionKey: string } | { readonly kind: "snapshot"; readonly versionKey: string };

const S = strings.decks;

export function DecksPage() {
  const { hasRole } = useCallerPermissions();
  const canPublish = hasRole(...PUBLISH_ROLES);

  const [view, setView] = useState<DecksView>({ kind: "list" });

  const params = useMemo<ListVersionsParams>(() => ({ pageSize: 50 }), []);
  const { page, loading, error, refresh } = useDeckVersions(params);

  return (
    <section className="eg-decks">
      <h1>{S.heading}</h1>

      {canPublish && view.kind === "list" && <DeckGeneratePanel onPublished={refresh} />}

      {view.kind === "list" && (
        <>
          <h2>{S.versions.heading}</h2>

          {loading && !page && (
            <div role="status" aria-live="polite">
              {S.versions.loading}
            </div>
          )}

          {error && (
            <div role="alert">
              <p>{S.versions.errorTitle}</p>
              <p>{error.message}</p>
            </div>
          )}

          {page && (
            <DeckVersionList
              versions={page.items}
              onOpenDetail={(versionKey) => setView({ kind: "detail", versionKey })}
              onOpenSnapshot={(versionKey) => setView({ kind: "snapshot", versionKey })}
            />
          )}
        </>
      )}

      {view.kind === "detail" && (
        <DeckVersionDetail versionKey={view.versionKey} canFinalize={canPublish} onBack={() => setView({ kind: "list" })} />
      )}

      {view.kind === "snapshot" && <FrozenRoadmapPanel versionKey={view.versionKey} onBack={() => setView({ kind: "list" })} />}
    </section>
  );
}
